// Per-dialect synthesizer for CREATE / ALTER / DROP {PROCEDURE|FUNCTION}
// (v2 Slice D7).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D7.
//
// Shell-only — `bodyText` is opaque verbatim text the resolver does not
// re-parse. The synthesizer emits the engine-specific prologue (CREATE
// keywords, parameter list, returns, options) and concatenates `bodyText`
// byte-for-byte after the engine-specific separator.
//
// Per-engine surface:
//   - mssql:  CREATE [OR ALTER] {PROCEDURE|FUNCTION} <name> [(params)]
//             [RETURNS <type|TABLE>] [WITH option, …] AS <body>
//             — function params are IN-only.
//   - pgsql:  CREATE [OR REPLACE] {PROCEDURE|FUNCTION} <name>(params)
//             [RETURNS <type>] [LANGUAGE …] [VOLATILITY] [STRICT]
//             [SECURITY {DEFINER|INVOKER}] AS <delim>body<delim>
//   - mysql:  CREATE [DEFINER = …] {PROCEDURE|FUNCTION} <name>(params)
//             [RETURNS <type>] [DETERMINISTIC] [{NO SQL|READS SQL DATA|
//             MODIFIES SQL DATA|CONTAINS SQL}] [SQL SECURITY {DEFINER|INVOKER}]
//             <body>
//             — function params are IN-only.
//   - oracle: CREATE [OR REPLACE] [EDITIONABLE|NONEDITIONABLE]
//             {PROCEDURE|FUNCTION} <name>(params) [RETURN <type>]
//             [AUTHID {DEFINER|CURRENT_USER}] AS <body>
//   - sqlite: not supported — no stored routines.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type {
  RoutineChange,
  RoutineKindShell,
  RoutineParamDef,
  RoutineShellDef,
} from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

function nameText(engine: SqlEngine, name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  const bare = maybeQuoteIdent(engine, name.name, name.quoted);
  if (name.schema) {
    const schema = maybeQuoteIdent(engine, name.schema, name.quoted);
    return `${schema}.${bare}`;
  }
  return bare;
}

function rejectField(engine: SqlEngine, op: string, field: string): never {
  throw new Error(`applyDdlEdit: ${engine} ${op} does not accept '${field}'`);
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

function kindKeyword(kind: RoutineKindShell): string {
  return kind === 'procedure' ? 'PROCEDURE' : 'FUNCTION';
}

function modeKeyword(mode: RoutineParamDef['mode']): string {
  if (mode === 'out') return 'OUT';
  if (mode === 'inout') return 'INOUT';
  return 'IN';
}

function validateBody(r: RoutineShellDef): void {
  if (r.bodyText.trim().length === 0) {
    throw new Error(
      `applyDdlEdit: create-routine requires a non-empty bodyText (the routine body)`,
    );
  }
}

function rejectFunctionOutParams(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.kind !== 'function') return;
  for (const p of r.params) {
    if (p.mode === 'out' || p.mode === 'inout') {
      throw new Error(
        `applyDdlEdit: ${engine} create-routine: function params are IN-only `
          + `(param '${p.name}' has mode '${p.mode}'). Use kind:'procedure' `
          + `for OUT / INOUT parameters.`,
      );
    }
  }
}

function rejectFunctionMissingReturns(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.kind === 'function' && !r.returnsText) {
    throw new Error(
      `applyDdlEdit: ${engine} create-routine: function requires 'returnsText' `
        + `(every supporting engine's CREATE FUNCTION mandates a return type).`,
    );
  }
}

function rejectProcedureReturns(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.kind === 'procedure' && r.returnsText) {
    throw new Error(
      `applyDdlEdit: ${engine} create-routine: procedure must not carry `
        + `'returnsText' (only functions return values).`,
    );
  }
}

// ─── CREATE ROUTINE ───────────────────────────────────────────────────────

export function synthesizeCreateRoutine(
  engine: SqlEngine,
  routine: RoutineShellDef,
): string {
  if (routine.oracleEditionable && routine.oracleNonEditionable) {
    throw new Error(
      `applyDdlEdit: oracle create-routine: 'oracleEditionable' and `
        + `'oracleNonEditionable' are mutually exclusive`,
    );
  }
  if (engine === 'mssql') return synthMssqlRoutine(routine);
  if (engine === 'postgres') return synthPgsqlRoutine(routine);
  if (engine === 'mysql') return synthMysqlRoutine(routine);
  if (engine === 'oracle') return synthOracleRoutine(routine);
  unsupported('sqlite', 'create-routine',
    `sqlite has no stored routines (no CREATE PROCEDURE / FUNCTION). `
      + `Move the logic to application code, or use a SQL view if read-only.`);
}

// ─── per-engine field-rejection helpers ───────────────────────────────────

function rejectNonMssqlFields(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.mssqlOrAlter) rejectField(engine, 'create-routine', 'mssqlOrAlter');
  if (r.mssqlWithOptions !== undefined && r.mssqlWithOptions.length > 0) {
    rejectField(engine, 'create-routine', 'mssqlWithOptions');
  }
}

function rejectNonPgsqlFields(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.pgsqlLanguage) rejectField(engine, 'create-routine', 'pgsqlLanguage');
  if (r.pgsqlVolatility) rejectField(engine, 'create-routine', 'pgsqlVolatility');
  if (r.pgsqlStrict) rejectField(engine, 'create-routine', 'pgsqlStrict');
  if (r.pgsqlSecurity) rejectField(engine, 'create-routine', 'pgsqlSecurity');
  if (r.pgsqlBodyDelimiter) {
    rejectField(engine, 'create-routine', 'pgsqlBodyDelimiter');
  }
}

function rejectNonMysqlFields(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.mysqlDefiner) rejectField(engine, 'create-routine', 'mysqlDefiner');
  if (r.mysqlDeterministic) {
    rejectField(engine, 'create-routine', 'mysqlDeterministic');
  }
  if (r.mysqlSqlAccess) rejectField(engine, 'create-routine', 'mysqlSqlAccess');
  if (r.mysqlSqlSecurity) {
    rejectField(engine, 'create-routine', 'mysqlSqlSecurity');
  }
}

function rejectNonOracleFields(engine: SqlEngine, r: RoutineShellDef): void {
  if (r.oracleEditionable) {
    rejectField(engine, 'create-routine', 'oracleEditionable');
  }
  if (r.oracleNonEditionable) {
    rejectField(engine, 'create-routine', 'oracleNonEditionable');
  }
  if (r.oracleAuthid) rejectField(engine, 'create-routine', 'oracleAuthid');
}

// ─── parameter-list emit ──────────────────────────────────────────────────

function paramText(engine: SqlEngine, p: RoutineParamDef): string {
  const nm = maybeQuoteIdent(engine, p.name, p.quoted ?? false);
  const mode = p.mode ?? 'in';
  if (engine === 'mssql') {
    // mssql: @name TYPE [= default] [OUT]
    let s = `@${nm} ${p.typeText}`;
    if (p.defaultExpr) s += ` = ${p.defaultExpr}`;
    if (mode === 'out' || mode === 'inout') s += ' OUT';
    return s;
  }
  if (engine === 'postgres') {
    // pgsql: [mode] name TYPE [DEFAULT expr]
    let s = '';
    if (mode !== 'in') s += `${modeKeyword(mode)} `;
    s += `${nm} ${p.typeText}`;
    if (p.defaultExpr) s += ` DEFAULT ${p.defaultExpr}`;
    return s;
  }
  if (engine === 'mysql') {
    // mysql: [mode] name TYPE  (no DEFAULT in routine params)
    if (p.defaultExpr) {
      throw new Error(
        `applyDdlEdit: mysql create-routine: parameter default expressions `
          + `are not supported in mysql routine parameter lists (param '${p.name}'). `
          + `Move the default into the routine body via 'IF param IS NULL THEN …'.`,
      );
    }
    let s = '';
    if (mode !== 'in') s += `${modeKeyword(mode)} `;
    s += `${nm} ${p.typeText}`;
    return s;
  }
  // oracle: name [mode] TYPE [DEFAULT expr]
  let s = `${nm} `;
  if (mode === 'out') s += 'OUT ';
  else if (mode === 'inout') s += 'IN OUT ';
  s += p.typeText;
  if (p.defaultExpr) s += ` DEFAULT ${p.defaultExpr}`;
  return s;
}

function paramList(engine: SqlEngine, params: readonly RoutineParamDef[]): string {
  return params.map((p) => paramText(engine, p)).join(', ');
}

// ─── per-engine CREATE ROUTINE synth ──────────────────────────────────────

function synthMssqlRoutine(r: RoutineShellDef): string {
  validateBody(r);
  rejectFunctionOutParams('mssql', r);
  rejectFunctionMissingReturns('mssql', r);
  rejectProcedureReturns('mssql', r);
  if (r.orReplace) rejectField('mssql', 'create-routine', 'orReplace');
  rejectNonPgsqlFields('mssql', r);
  rejectNonMysqlFields('mssql', r);
  rejectNonOracleFields('mssql', r);
  let s = r.mssqlOrAlter ? 'CREATE OR ALTER ' : 'CREATE ';
  s += `${kindKeyword(r.kind)} ${nameText('mssql', r.name)}`;
  if (r.params.length > 0) s += ` (${paramList('mssql', r.params)})`;
  if (r.returnsText) s += ` RETURNS ${r.returnsText}`;
  if (r.mssqlWithOptions && r.mssqlWithOptions.length > 0) {
    s += ` WITH ${r.mssqlWithOptions.join(', ')}`;
  }
  s += ` AS ${r.bodyText.trim()}`;
  return s;
}

function synthPgsqlRoutine(r: RoutineShellDef): string {
  validateBody(r);
  rejectFunctionOutParams('postgres', r);
  rejectFunctionMissingReturns('postgres', r);
  rejectProcedureReturns('postgres', r);
  if (r.kind === 'procedure') {
    if (r.pgsqlVolatility) {
      rejectField('postgres', 'create-routine', 'pgsqlVolatility');
    }
    if (r.pgsqlStrict) {
      rejectField('postgres', 'create-routine', 'pgsqlStrict');
    }
  }
  if (!r.pgsqlLanguage) {
    throw new Error(
      `applyDdlEdit: postgres create-routine requires 'pgsqlLanguage' `
        + `(e.g. 'plpgsql', 'sql'). pgsql CREATE FUNCTION / PROCEDURE has no `
        + `default LANGUAGE.`,
    );
  }
  rejectNonMssqlFields('postgres', r);
  rejectNonMysqlFields('postgres', r);
  rejectNonOracleFields('postgres', r);
  let s = r.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  s += `${kindKeyword(r.kind)} ${nameText('postgres', r.name)}`;
  s += `(${paramList('postgres', r.params)})`;
  if (r.returnsText) s += ` RETURNS ${r.returnsText}`;
  s += ` LANGUAGE ${r.pgsqlLanguage}`;
  if (r.pgsqlVolatility === 'volatile') s += ' VOLATILE';
  else if (r.pgsqlVolatility === 'stable') s += ' STABLE';
  else if (r.pgsqlVolatility === 'immutable') s += ' IMMUTABLE';
  if (r.pgsqlStrict) s += ' STRICT';
  if (r.pgsqlSecurity === 'definer') s += ' SECURITY DEFINER';
  else if (r.pgsqlSecurity === 'invoker') s += ' SECURITY INVOKER';
  const delim = r.pgsqlBodyDelimiter ?? '$$';
  s += ` AS ${delim}${r.bodyText}${delim}`;
  return s;
}

function synthMysqlRoutine(r: RoutineShellDef): string {
  validateBody(r);
  rejectFunctionOutParams('mysql', r);
  rejectFunctionMissingReturns('mysql', r);
  rejectProcedureReturns('mysql', r);
  if (r.orReplace) rejectField('mysql', 'create-routine', 'orReplace');
  rejectNonMssqlFields('mysql', r);
  rejectNonPgsqlFields('mysql', r);
  rejectNonOracleFields('mysql', r);
  let s = 'CREATE ';
  if (r.mysqlDefiner) s += `DEFINER = ${r.mysqlDefiner} `;
  s += `${kindKeyword(r.kind)} ${nameText('mysql', r.name)}`;
  s += `(${paramList('mysql', r.params)})`;
  if (r.returnsText) s += ` RETURNS ${r.returnsText}`;
  if (r.mysqlDeterministic) s += ' DETERMINISTIC';
  if (r.mysqlSqlAccess === 'no-sql') s += ' NO SQL';
  else if (r.mysqlSqlAccess === 'reads-sql-data') s += ' READS SQL DATA';
  else if (r.mysqlSqlAccess === 'modifies-sql-data') s += ' MODIFIES SQL DATA';
  else if (r.mysqlSqlAccess === 'contains-sql') s += ' CONTAINS SQL';
  if (r.mysqlSqlSecurity === 'definer') s += ' SQL SECURITY DEFINER';
  else if (r.mysqlSqlSecurity === 'invoker') s += ' SQL SECURITY INVOKER';
  s += ` ${r.bodyText.trim()}`;
  return s;
}

function synthOracleRoutine(r: RoutineShellDef): string {
  validateBody(r);
  rejectFunctionMissingReturns('oracle', r);
  rejectProcedureReturns('oracle', r);
  rejectNonMssqlFields('oracle', r);
  rejectNonPgsqlFields('oracle', r);
  rejectNonMysqlFields('oracle', r);
  let s = r.orReplace ? 'CREATE OR REPLACE ' : 'CREATE ';
  if (r.oracleEditionable) s += 'EDITIONABLE ';
  if (r.oracleNonEditionable) s += 'NONEDITIONABLE ';
  s += `${kindKeyword(r.kind)} ${nameText('oracle', r.name)}`;
  s += `(${paramList('oracle', r.params)})`;
  if (r.returnsText) s += ` RETURN ${r.returnsText}`;
  if (r.oracleAuthid === 'definer') s += ' AUTHID DEFINER';
  else if (r.oracleAuthid === 'current-user') s += ' AUTHID CURRENT_USER';
  s += ` AS ${r.bodyText.trim()}`;
  return s;
}

// ─── ALTER ROUTINE (compile / rename) ─────────────────────────────────────

export function synthesizeAlterRoutine(
  engine: SqlEngine,
  routineName: QualifiedName,
  routineKind: RoutineKindShell,
  change: RoutineChange,
): string {
  if (engine === 'sqlite') {
    unsupported('sqlite', 'alter-routine',
      `sqlite has no stored routines.`);
  }
  if (engine === 'mysql') {
    unsupported('mysql', 'alter-routine',
      `mysql ALTER PROCEDURE / FUNCTION only changes characteristics; for `
        + `body / signature changes drop and recreate via op:'drop-routine' + `
        + `op:'create-routine'. v2 surfaces only the portable rename + recompile `
        + `shapes — neither is supported on mysql.`);
  }
  if (engine === 'mssql') {
    unsupported('mssql', 'alter-routine',
      `mssql has no portable ALTER PROCEDURE / FUNCTION shape — redefine via `
        + `op:'create-routine' with mssqlOrAlter:true (CREATE OR ALTER), or use `
        + `sp_rename for renames.`);
  }
  const hasRecompile = change.recompile === true;
  const hasRename = change.renameTo !== undefined;
  if (!hasRecompile && !hasRename) {
    throw new Error(
      `applyDdlEdit: alter-routine requires either 'recompile: true' (oracle) `
        + `or 'renameTo' (postgres).`,
    );
  }
  if (hasRecompile && hasRename) {
    throw new Error(
      `applyDdlEdit: alter-routine accepts 'recompile' OR 'renameTo' but not `
        + `both — emit two separate edits.`,
    );
  }
  if (engine === 'oracle') {
    if (hasRename) {
      unsupported('oracle', 'alter-routine renameTo',
        `oracle has no ALTER PROCEDURE / FUNCTION RENAME — use Oracle's `
          + `RENAME statement (RENAME old_name TO new_name) at the object level, `
          + `or drop and recreate.`);
    }
    return `ALTER ${kindKeyword(routineKind)} ${nameText('oracle', routineName)} COMPILE`;
  }
  // postgres
  if (hasRecompile) {
    unsupported('postgres', 'alter-routine recompile',
      `postgres has no explicit recompile — functions are JIT-compiled on use. `
        + `If you need to bust the plan cache, drop and recreate.`);
  }
  return `ALTER ${kindKeyword(routineKind)} ${nameText('postgres', routineName)} `
    + `RENAME TO ${nameText('postgres', change.renameTo!)}`;
}

// ─── DROP ROUTINE ─────────────────────────────────────────────────────────

export function synthesizeDropRoutine(
  engine: SqlEngine,
  routineName: QualifiedName,
  routineKind: RoutineKindShell,
  ifExists: boolean,
  cascade: boolean,
  pgsqlSignature: string | undefined,
): string {
  if (engine === 'sqlite') {
    unsupported('sqlite', 'drop-routine',
      `sqlite has no stored routines.`);
  }
  if (engine !== 'postgres') {
    if (cascade) rejectField(engine, 'drop-routine', 'cascade');
    if (pgsqlSignature) rejectField(engine, 'drop-routine', 'pgsqlSignature');
  }
  if (ifExists && engine === 'oracle') {
    throw new Error(
      `applyDdlEdit: oracle drop-routine does not accept 'ifExists' — `
        + `oracle DROP PROCEDURE / FUNCTION has no IF EXISTS keyword. Wrap in `
        + `PL/SQL with USER_PROCEDURES lookup at the consumer level.`,
    );
  }
  let s = `DROP ${kindKeyword(routineKind)} `;
  if (ifExists) s += 'IF EXISTS ';
  s += nameText(engine, routineName);
  if (engine === 'postgres' && pgsqlSignature) s += pgsqlSignature;
  if (engine === 'postgres' && cascade) s += ' CASCADE';
  return s;
}
